import React, { useEffect, useMemo, useState } from 'react';
import type { ConnectorDataset, TrainingPreviewResponse } from '../../types/api';
import type { AgentConfiguratorState } from '../../types/agentConfigurator';
import type { PipelineTask } from '../../types/pipeline';
import type { VirtualUser } from '../../types/vuser';
import type { OptimizationPolicyUiState } from '../../utils/optimizationPolicy';
import type { OptimizationPreviewData } from '../../types/optimizationPreview';
import type { DatasetGenerationPredictTokenUsageResponse } from '../../types/datasetGenerationPreview';
import { autoTune, connector, vuser } from '../../services/api';
import OptimizationPreviewResultView from '../optimization/OptimizationPreviewResultView';
import TrainingPreviewResultView from '../training/TrainingPreviewResultView';
import DatasetGenerationTokenPreviewView from '../dataset/DatasetGenerationTokenPreviewView';
import '../dataset/DatasetGenerationTokenPreviewView.css';
import {
  buildPipelineConfigTuningPreviewRequest,
  buildPipelineFineTuningPreviewBody,
  resolveConnectorDatasetRegistryId,
  resolvePipelineFineTuningPreviewDatasetIds,
  resolvePipelinePreviewDatasetNames,
} from '../../utils/pipelinePreviewRequest';
import {
  buildAssistantConfigFromConfigurator,
  buildDatasetGenerationPredictTokenUsageRequest,
  buildVuserConfigPayload,
} from '../../utils/datasetGenerationPreviewRequest';
import {
  formatOptimizationJobStartError,
  formatTrainingJobFailedAgentsErrorMessage,
  formatTrainingJobUserErrorMessage,
  getErrorMessage,
} from '../../utils/errorUtils';
import {
  buildReactionWeightsFromForm,
  EMPTY_REACTION_WEIGHTS_FORM,
  type ReactionWeightsFormState,
} from '../../types/reactionWeights';
import FineTuningReactionWeightsFields from '../FineTuningReactionWeightsFields';
import HistoryContextControl, { BoolToggleRow } from '../ui/HistoryContextControl';
import DateTimePicker from '../ui/DateTimePicker';
import MultiSelect, { type MultiSelectOption } from './MultiSelect';
import ConnectorConfigTuningFields from '../ConnectorConfigTuningFields';
import {
  filterRegistryDatasetsForSelectStep,
  formatPipelineTaskType,
  getTaskCatalogItem,
  normalizePipelineTaskType,
} from '../../utils/pipelineDefaults';
import {
  FINE_TUNING_MODEL_NAME_MAX_LEN,
  listFineTuningBaseModelGroups,
  resolveFineTuningModelNamePrefix,
} from '../../utils/fineTuningAgentPayload';
import type { EvaluationMode, SelectionScenario } from '../../types/optimizationJob';
import { normalizeOptimizationApproach } from '../../types/optimizationApproach';
import {
  buildEvaluatorParamsPayload,
  deriveOptimizeByTokenFromSelectionScenario,
  mergeEvaluatorParamsFormForMode,
  normalizeEvaluationMode,
  parseEvaluatorParamsForm,
  resolveSelectionScenario,
} from '../../types/optimizationJob';
import type { EvaluatorParamsFormState } from '../../types/evaluation';
import { getAvailableModelsForConfigurator } from '../../utils/configuratorModels';
import {
  defaultTrainingPeriodRange,
  getTrainingPeriodFromConfig,
  hasTrainingPeriodObject,
  isTrainingPeriodComplete,
  normalizePipelinePeriodDaysForSave,
  PIPELINE_PERIOD_DAYS_DEFAULT,
  PIPELINE_PERIOD_DAYS_MAX,
  PIPELINE_PERIOD_DAYS_MIN,
  validatePipelineTaskFineTuning,
  validatePipelineTaskPeriodDays,
  validatePipelineTaskTrainingPeriod,
  validatePipelineTaskVirtualUsers,
} from '../../utils/pipelineTrainingPeriod';
import {
  commitBoundedDecimalDraft,
  commitPositiveIntegerDraft,
  numberInputDisplay,
  resolveNumberInput,
  sanitizeBoundedDecimalDraft,
  sanitizePositiveIntegerDraft,
} from '../../utils/numberInputDraft';
import {
  DATASET_GENERATION_MAX_DIALOGS,
  DATASET_GENERATION_MAX_MESSAGES_PER_DIALOG,
  DATASET_GENERATION_MIN_DIALOGS,
  DATASET_GENERATION_MIN_MESSAGES_PER_DIALOG,
  UNIT_RATIO_MAX,
  UNIT_RATIO_MIN,
  UNIT_RATIO_STEP,
} from '../../constants/datasetGenerationLimits';
import PipelineToggleSwitch from './PipelineToggleSwitch';
import './PipelineTaskConfigEditor.css';

function cfgNum(value: unknown, fallback: number): number {
  return resolveNumberInput(value, fallback);
}

function cfgStr(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

function cfgBool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function reactionWeightsToForm(raw: unknown): ReactionWeightsFormState {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_REACTION_WEIGHTS_FORM };
  const w = raw as { admin?: { like?: number; dislike?: number; propagation?: number }; user?: { like?: number; dislike?: number; propagation?: number } };
  return {
    admin_like: w.admin?.like != null ? String(w.admin.like) : '',
    admin_dislike: w.admin?.dislike != null ? String(w.admin.dislike) : '',
    admin_propagation: w.admin?.propagation != null ? String(w.admin.propagation) : '',
    user_like: w.user?.like != null ? String(w.user.like) : '',
    user_dislike: w.user?.dislike != null ? String(w.user.dislike) : '',
    user_propagation: w.user?.propagation != null ? String(w.user.propagation) : '',
  };
}

function patchConfig(task: PipelineTask, patch: Record<string, unknown>, onChange: (next: PipelineTask) => void) {
  const nextConfig = { ...task.config, ...patch };
  if ('groundtruth' in patch) {
    delete nextConfig.ground_truth;
  }
  onChange({ ...task, config: nextConfig });
}

function patchPeriodDays(
  task: PipelineTask,
  raw: string,
  onChange: (next: PipelineTask) => void,
  extra?: Record<string, unknown>
) {
  if (raw === '') {
    patchConfig(task, { ...extra, period_days: '' }, onChange);
    return;
  }
  const sanitized = sanitizePositiveIntegerDraft(raw, PIPELINE_PERIOD_DAYS_MIN, PIPELINE_PERIOD_DAYS_MAX);
  if (sanitized === '') {
    patchConfig(task, { ...extra, period_days: '' }, onChange);
    return;
  }
  patchConfig(task, { ...extra, period_days: Number(sanitized) }, onChange);
}

function commitPeriodDays(
  task: PipelineTask,
  raw: string,
  onChange: (next: PipelineTask) => void,
  extra?: Record<string, unknown>
) {
  const committed = commitPositiveIntegerDraft(raw, PIPELINE_PERIOD_DAYS_MIN, PIPELINE_PERIOD_DAYS_MAX);
  const value =
    committed === '' ? PIPELINE_PERIOD_DAYS_DEFAULT : Number(committed);
  patchConfig(task, { ...extra, period_days: value }, onChange);
}

function datasetSourceLabel(row: ConnectorDataset): string {
  const meta = row.metadata;
  const source =
    meta && typeof meta === 'object' && !Array.isArray(meta)
      ? String((meta as Record<string, unknown>).source || '')
      : '';
  const label = typeof row.label === 'string' ? row.label.trim() : '';
  const created = row.created_at || row.createdAt;
  const date = created ? new Date(String(created)).toLocaleDateString() : '';
  return [label || 'Untitled', source ? `(${source})` : '', date].filter(Boolean).join(' ');
}

function datasetLabelValue(row: ConnectorDataset): string {
  const label = typeof row.label === 'string' ? row.label.trim() : '';
  return label || String(row.id || '');
}

function PipelinePreviewDatasetPicker({
  id,
  label,
  hint,
  options,
  selected,
  onChange,
  loading,
}: {
  id: string;
  label: string;
  hint?: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  loading?: boolean;
}) {
  return (
    <div className="pipeline-task-config__field pipeline-task-config__field--wide">
      <label className="pipeline-task-config__label" htmlFor={id}>
        {label}
      </label>
      <MultiSelect
        options={options}
        value={selected}
        onChange={onChange}
        placeholder={loading ? 'Loading datasets…' : 'Select datasets for preview'}
        disabled={loading}
        portal
        maxDropdownHeight={480}
        dropdownClassName="multi-select-dropdown--pipeline-datasets"
      />
      {hint ? <p className="pipeline-task-config__muted">{hint}</p> : null}
    </div>
  );
}

const REGISTRY_DATASET_PREVIEW_LIMIT = 12;

function RegistryDatasetsPreview({
  datasets,
  loading,
}: {
  datasets: ConnectorDataset[];
  loading?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const previewKey = useMemo(
    () => datasets.map((row, index) => String(row.id ?? row.label ?? index)).join('|'),
    [datasets]
  );

  useEffect(() => {
    setExpanded(false);
  }, [previewKey]);

  const hiddenCount = Math.max(0, datasets.length - REGISTRY_DATASET_PREVIEW_LIMIT);
  const visibleDatasets = expanded ? datasets : datasets.slice(0, REGISTRY_DATASET_PREVIEW_LIMIT);

  return (
    <div className="pipeline-task-config__registry">
      <div className="pipeline-task-config__registry-head">
        <span className="pipeline-task-config__label">
          Registry datasets (preview, filtered by type &amp; period)
        </span>
        {!loading && datasets.length > 0 ? (
          <span className="pipeline-task-config__registry-count">{datasets.length}</span>
        ) : null}
      </div>
      {loading ? (
        <p className="pipeline-task-config__muted">Loading…</p>
      ) : datasets.length === 0 ? (
        <p className="pipeline-task-config__muted">
          No matching datasets in registry for the current filters.
        </p>
      ) : (
        <>
          <ul
            className={`pipeline-task-config__dataset-list${
              expanded ? ' pipeline-task-config__dataset-list--scroll' : ''
            }`}
          >
            {visibleDatasets.map((row, i) => {
              const id = String(row.id ?? i);
              return (
                <li key={id}>
                  <span>{datasetSourceLabel(row)}</span>
                </li>
              );
            })}
          </ul>
          {hiddenCount > 0 ? (
            <button
              type="button"
              className="pipeline-task-config__expand-btn"
              onClick={() => setExpanded((value) => !value)}
              aria-expanded={expanded}
            >
              {expanded ? 'Show less' : `Show all ${datasets.length} datasets`}
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}

export interface PipelineTaskConfigEditorProps {
  task: PipelineTask;
  taskIndex: number;
  onChange: (next: PipelineTask) => void;
  integrationId: string;
  virtualUsers: VirtualUser[];
  connectorDatasets: ConnectorDataset[];
  connectorDatasetsLoading?: boolean;
  configFromConfigurator: AgentConfiguratorState | null;
  optimizationPolicyUi: OptimizationPolicyUiState;
  onOptimizationPolicyUiChange: (next: OptimizationPolicyUiState) => void;
  optimizationIterations: number;
  onOptimizationIterationsChange: (n: number) => void;
  hasSelectTrainingStep: boolean;
}

function patchTrainingPeriod(
  task: PipelineTask,
  config: Record<string, unknown>,
  patch: Partial<{ from: string; to: string }>,
  onChange: (next: PipelineTask) => void,
  extra?: Record<string, unknown>
) {
  const current = getTrainingPeriodFromConfig(config);
  patchConfig(
    task,
    {
      ...extra,
      training_period: {
        from: patch.from ?? current.from ?? '',
        to: patch.to ?? current.to ?? '',
      },
    },
    onChange
  );
}

function enableTrainingPeriodDateRange(
  task: PipelineTask,
  config: Record<string, unknown>,
  onChange: (next: PipelineTask) => void,
  extra?: Record<string, unknown>
) {
  const { period_days: _periodDays, training_period: _existing, ...rest } = config;
  const current = getTrainingPeriodFromConfig(config);
  const training_period = isTrainingPeriodComplete(current)
    ? { from: String(current.from), to: String(current.to) }
    : defaultTrainingPeriodRange(cfgNum(config.period_days, 30));
  onChange({
    ...task,
    config: {
      ...rest,
      ...extra,
      training_period,
    },
  });
}

function disableTrainingPeriodDateRange(
  task: PipelineTask,
  config: Record<string, unknown>,
  onChange: (next: PipelineTask) => void,
  extra?: Record<string, unknown>
) {
  const { training_period: _removed, ...rest } = config;
  onChange({
    ...task,
    config: {
      ...rest,
      ...extra,
      period_days: normalizePipelinePeriodDaysForSave(config.period_days),
    },
  });
}

function PeriodModeToggle({
  id,
  useDateRange,
  onSelectDays,
  onSelectRange,
  disabled = false,
}: {
  id: string;
  useDateRange: boolean;
  onSelectDays: () => void;
  onSelectRange: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="pipeline-period-toggle">
      <span className="pipeline-task-config__label" id={`${id}-label`}>
        Time period
      </span>
      <div
        className="pipeline-period-toggle__segment"
        role="radiogroup"
        aria-labelledby={`${id}-label`}
      >
        <button
          type="button"
          role="radio"
          aria-checked={!useDateRange}
          className={`pipeline-period-toggle__btn${!useDateRange ? ' pipeline-period-toggle__btn--active' : ''}`}
          disabled={disabled}
          onClick={onSelectDays}
        >
          <i className="fas fa-clock" aria-hidden />
          Last N days
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={useDateRange}
          className={`pipeline-period-toggle__btn${useDateRange ? ' pipeline-period-toggle__btn--active' : ''}`}
          disabled={disabled}
          onClick={onSelectRange}
        >
          <i className="fas fa-calendar-days" aria-hidden />
          Custom range
        </button>
      </div>
      <p className="pipeline-period-toggle__hint">
        {useDateRange
          ? 'Pick exact From / To dates for the dataset window.'
          : 'Rolling window — e.g. last 30 days from today.'}
      </p>
    </div>
  );
}

const PipelineTaskConfigEditor: React.FC<PipelineTaskConfigEditorProps> = ({
  task,
  taskIndex,
  onChange,
  integrationId,
  virtualUsers,
  connectorDatasets,
  connectorDatasetsLoading,
  configFromConfigurator,
  optimizationPolicyUi,
  onOptimizationPolicyUiChange,
  optimizationIterations,
  onOptimizationIterationsChange,
  hasSelectTrainingStep,
}) => {
  const type = normalizePipelineTaskType(task.type);
  const catalog = getTaskCatalogItem(type);
  const c = task.config;
  const fieldsDisabled = !task.enabled;

  const userOptions: MultiSelectOption[] = useMemo(
    () =>
      virtualUsers
        .filter((u) => u.type === 'user' && u.id)
        .map((u) => {
          const baseName = u.name?.trim() || u.description?.trim() || u.id;
          return {
            value: u.id,
            label: u.persona_id ? baseName : `${baseName} (no persona)`,
          };
        }),
    [virtualUsers]
  );

  const adminOptions: MultiSelectOption[] = useMemo(
    () =>
      virtualUsers
        .filter((u) => u.type === 'admin' && u.id)
        .map((u) => ({
          value: u.id,
          label: u.name?.trim() || u.description?.trim() || u.id,
        })),
    [virtualUsers]
  );

  const configuratorAvailableModels = useMemo(
    () => getAvailableModelsForConfigurator(integrationId),
    [integrationId]
  );

  const datasetTypes = Array.isArray(c.dataset_types)
    ? (c.dataset_types as string[])
    : ['integration', 'vuser'];
  const selectUseDateRange = hasTrainingPeriodObject(c);
  const selectPeriod = getTrainingPeriodFromConfig(c);
  const periodValidationError = validatePipelineTaskTrainingPeriod(type, c as Record<string, unknown>);
  const periodDaysValidationError =
    !selectUseDateRange && (type === 'load_integration_datasets' || type === 'select_training_datasets')
      ? validatePipelineTaskPeriodDays(type, c as Record<string, unknown>)
      : null;
  const vuserValidationError =
    type === 'generate_vuser_datasets' && task.enabled
      ? validatePipelineTaskVirtualUsers(type, c as Record<string, unknown>, virtualUsers)
      : null;
  const fineTuningValidationError =
    type === 'fine_tuning' && task.enabled
      ? validatePipelineTaskFineTuning(type, c as Record<string, unknown>)
      : null;

  const connectorDatasetLabelOptions: MultiSelectOption[] = useMemo(
    () =>
      connectorDatasets
        .map((row) => {
          const value = datasetLabelValue(row);
          if (!value) return null;
          return { value, label: datasetSourceLabel(row) };
        })
        .filter((opt): opt is MultiSelectOption => Boolean(opt)),
    [connectorDatasets]
  );

  const connectorDatasetIdOptions: MultiSelectOption[] = useMemo(
    () =>
      connectorDatasets
        .map((row) => {
          const id = resolveConnectorDatasetRegistryId(row);
          if (!id) return null;
          return {
            value: id,
            label: datasetSourceLabel(row),
          };
        })
        .filter((opt): opt is MultiSelectOption => Boolean(opt)),
    [connectorDatasets]
  );

  const [configPreviewDatasetNames, setConfigPreviewDatasetNames] = useState<string[]>([]);
  const [configPreviewLoading, setConfigPreviewLoading] = useState(false);
  const [configPreviewError, setConfigPreviewError] = useState<string | null>(null);
  const [configPreviewResult, setConfigPreviewResult] = useState<OptimizationPreviewData | null>(null);

  const [ftPreviewDatasetIds, setFtPreviewDatasetIds] = useState<string[]>([]);
  const [ftPreviewLoading, setFtPreviewLoading] = useState(false);
  const [ftPreviewError, setFtPreviewError] = useState<string | null>(null);
  const [ftPreviewResult, setFtPreviewResult] = useState<TrainingPreviewResponse | null>(null);

  const [vuserTokenPreviewLoading, setVuserTokenPreviewLoading] = useState(false);
  const [vuserTokenPreviewError, setVuserTokenPreviewError] = useState<string | null>(null);
  const [vuserTokenPreviewResult, setVuserTokenPreviewResult] =
    useState<DatasetGenerationPredictTokenUsageResponse | null>(null);

  useEffect(() => {
    setConfigPreviewDatasetNames([]);
    setConfigPreviewError(null);
    setConfigPreviewResult(null);
    setFtPreviewDatasetIds([]);
    setFtPreviewError(null);
    setFtPreviewResult(null);
    setVuserTokenPreviewError(null);
    setVuserTokenPreviewResult(null);
  }, [task.type, taskIndex]);

  const handleConfigTuningPreview = async () => {
    if (!configFromConfigurator) {
      setConfigPreviewError('Load the integration configurator before running preview.');
      return;
    }
    const datasetNames = resolvePipelinePreviewDatasetNames(task, configPreviewDatasetNames);
    if (datasetNames.length === 0) {
      setConfigPreviewError('Select at least one dataset for preview.');
      return;
    }
    const request = buildPipelineConfigTuningPreviewRequest({
      integrationId,
      task,
      configFromConfigurator,
      optimizationPolicyUi,
      optimizationIterations,
      datasetNames,
    });
    if (!request) {
      setConfigPreviewError('Could not build preview request from the current form.');
      return;
    }

    setConfigPreviewLoading(true);
    setConfigPreviewError(null);
    setConfigPreviewResult(null);
    try {
      const response = await autoTune.optimizePreview(request);
      if (response?.success === false) {
        setConfigPreviewError(formatOptimizationJobStartError(response, 'Preview failed'));
      } else {
        setConfigPreviewResult(response?.data ?? null);
      }
    } catch (e: unknown) {
      setConfigPreviewError(formatOptimizationJobStartError(e, 'Failed to load optimization preview.'));
    } finally {
      setConfigPreviewLoading(false);
    }
  };

  const handleFineTuningPreview = async () => {
    const datasetIds = resolvePipelineFineTuningPreviewDatasetIds(
      connectorDatasets,
      ftPreviewDatasetIds
    );
    const previewBuild = buildPipelineFineTuningPreviewBody({
      integrationId,
      task,
      datasetIds,
      configFromConfigurator,
    });
    if (!previewBuild.ok) {
      setFtPreviewError(previewBuild.error);
      return;
    }

    setFtPreviewLoading(true);
    setFtPreviewError(null);
    setFtPreviewResult(null);
    try {
      const resp = await connector.previewTrainingJob(previewBuild.body);
      setFtPreviewResult((resp?.data || resp) as TrainingPreviewResponse);
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: { message?: string; details?: { failed_agents?: Array<{ agent_name?: string; error?: string }> } } } };
      const status = err?.response?.status;
      const data = err?.response?.data;
      const failedAgents = data?.details?.failed_agents;
      if (status === 400 && Array.isArray(failedAgents) && failedAgents.length > 0) {
        setFtPreviewError(
          formatTrainingJobFailedAgentsErrorMessage({
            message: data?.message,
            failedAgents,
            fallbackMessage: 'None of the selected agents could be previewed for fine-tuning.',
          })
        );
      } else {
        setFtPreviewError(formatTrainingJobUserErrorMessage(getErrorMessage(e, 'Preview failed')));
      }
    } finally {
      setFtPreviewLoading(false);
    }
  };

  const handleVuserDatasetTokenPreview = async (
    dialog_count: number,
    message_count: number,
    vuIds: string[],
    vaIds: string[]
  ) => {
    if (!configFromConfigurator) {
      setVuserTokenPreviewError('Load the integration configurator before estimating tokens.');
      setVuserTokenPreviewResult(null);
      return;
    }
    if (dialog_count < 0 || message_count < 0) {
      setVuserTokenPreviewError('Dialog count and messages per dialog must be zero or greater.');
      setVuserTokenPreviewResult(null);
      return;
    }

    setVuserTokenPreviewLoading(true);
    setVuserTokenPreviewError(null);
    try {
      const body = buildDatasetGenerationPredictTokenUsageRequest({
        assistant_config: buildAssistantConfigFromConfigurator(configFromConfigurator),
        vuser_config: buildVuserConfigPayload(virtualUsers, vuIds),
        vadmin_config: buildVuserConfigPayload(virtualUsers, vaIds),
        dialog_count,
        message_count,
      });
      const result = await vuser.predictDatasetGenerationTokenUsage(integrationId, body);
      setVuserTokenPreviewResult(result);
    } catch (e: unknown) {
      setVuserTokenPreviewResult(null);
      setVuserTokenPreviewError(getErrorMessage(e, 'Token estimate failed'));
    } finally {
      setVuserTokenPreviewLoading(false);
    }
  };

  const vuserPreviewUserIds = Array.isArray(c.virtual_user_ids) ? (c.virtual_user_ids as string[]) : [];
  const vuserPreviewAdminIds = Array.isArray(c.virtual_admin_ids) ? (c.virtual_admin_ids as string[]) : [];
  const vuserPreviewUserIdsKey = vuserPreviewUserIds.join(',');
  const vuserPreviewAdminIdsKey = vuserPreviewAdminIds.join(',');

  useEffect(() => {
    if (type !== 'generate_vuser_datasets' || !task.enabled) {
      setVuserTokenPreviewResult(null);
      setVuserTokenPreviewError(null);
      setVuserTokenPreviewLoading(false);
      return;
    }
    if (!configFromConfigurator) {
      setVuserTokenPreviewResult(null);
      setVuserTokenPreviewError(null);
      return;
    }

    const dialog_count = cfgNum(c.dialog_count, 1);
    const message_count = cfgNum(c.messages_per_dialog, 5);
    const timer = window.setTimeout(() => {
      void handleVuserDatasetTokenPreview(
        dialog_count,
        message_count,
        vuserPreviewUserIds,
        vuserPreviewAdminIds
      );
    }, 500);

    return () => window.clearTimeout(timer);
  }, [
    type,
    task.enabled,
    configFromConfigurator,
    c.dialog_count,
    c.messages_per_dialog,
    vuserPreviewUserIdsKey,
    vuserPreviewAdminIdsKey,
    virtualUsers,
    integrationId,
  ]);

  const renderVuserTokenEstimatePanel = () => (
    <div className="pipeline-task-config__field pipeline-task-config__field--wide dataset-gen-token-estimate">
      <div className="dataset-gen-token-estimate__head">
        <h4 className="dataset-gen-token-estimate__title">
          <i className="fas fa-coins" aria-hidden />
          Estimated token usage
        </h4>
        {vuserTokenPreviewLoading ? (
          <span className="dataset-gen-token-estimate__status">
            <i className="fas fa-spinner fa-spin" aria-hidden /> Calculating…
          </span>
        ) : null}
      </div>
      {!configFromConfigurator ? (
        <p className="dataset-gen-token-estimate__status">
          Load the integration configurator to estimate tokens.
        </p>
      ) : vuserTokenPreviewError ? (
        <p className="dataset-gen-token-estimate__error" role="alert">
          {vuserTokenPreviewError}
        </p>
      ) : vuserTokenPreviewResult ? (
        <DatasetGenerationTokenPreviewView data={vuserTokenPreviewResult} />
      ) : (
        <p className="dataset-gen-token-estimate__status">
          Adjust dialog volume or virtual users to see an estimate.
        </p>
      )}
    </div>
  );

  const renderPreviewActions = (content: React.ReactNode) => (
    <div className="pipeline-task-config__preview">
      {content}
    </div>
  );

  const filteredRegistryDatasets = useMemo(() => {
    if (type !== 'select_training_datasets') return connectorDatasets;
    return filterRegistryDatasetsForSelectStep(connectorDatasets, datasetTypes, {
      periodDays: selectUseDateRange ? undefined : cfgNum(c.period_days, 30),
      trainingPeriod: selectUseDateRange
        ? { from: selectPeriod.from, to: selectPeriod.to }
        : undefined,
    });
  }, [
    type,
    connectorDatasets,
    datasetTypes,
    selectUseDateRange,
    c.period_days,
    selectPeriod.from,
    selectPeriod.to,
  ]);

  const renderFieldGrid = (children: React.ReactNode) => (
    <div className="pipeline-task-config__grid">{children}</div>
  );

  const renderLabel = (htmlFor: string, text: string) => (
    <label className="pipeline-task-config__label" htmlFor={htmlFor}>
      {text}
    </label>
  );

  const renderNumber = (
    id: string,
    label: string,
    value: unknown,
    fallback: number,
    onValue: (raw: string) => void,
    opts?: {
      min?: number;
      max?: number;
      step?: number;
      integer?: boolean;
      enforceBounds?: boolean;
    }
  ) => {
    const min = opts?.min;
    const max = opts?.max;
    const step = opts?.step ?? (opts?.integer ? 1 : undefined);
    const enforceBounds = opts?.enforceBounds ?? (min != null && max != null);

    const sanitize = (raw: string): string => {
      if (!enforceBounds || min == null || max == null) return raw;
      if (opts?.integer) return sanitizePositiveIntegerDraft(raw, min, max);
      return sanitizeBoundedDecimalDraft(raw, min, max);
    };

    const commit = (raw: string): string => {
      if (!enforceBounds || min == null || max == null) return raw;
      if (opts?.integer) return commitPositiveIntegerDraft(raw, min, max);
      return commitBoundedDecimalDraft(raw, min, max, step ?? UNIT_RATIO_STEP);
    };

    return (
      <div className="pipeline-task-config__field">
        {renderLabel(id, label)}
        <input
          id={id}
          type="number"
          className="pipeline-task-config__input"
          value={numberInputDisplay(value, fallback)}
          min={min}
          max={max}
          step={step}
          disabled={fieldsDisabled}
          onChange={(e) => onValue(sanitize(e.target.value))}
          onBlur={(e) => {
            const committed = commit(e.target.value);
            if (committed !== e.target.value) onValue(committed);
          }}
        />
      </div>
    );
  };

  const renderPeriodDays = (
    id: string,
    value: unknown,
    extra?: Record<string, unknown>
  ) => (
    <div className="pipeline-task-config__field">
      {renderLabel(id, 'Period days')}
      <input
        id={id}
        type="number"
        className="pipeline-task-config__input"
        value={numberInputDisplay(value, PIPELINE_PERIOD_DAYS_DEFAULT)}
        min={PIPELINE_PERIOD_DAYS_MIN}
        max={PIPELINE_PERIOD_DAYS_MAX}
        step={1}
        disabled={fieldsDisabled}
        onChange={(e) =>
          patchPeriodDays(
            task,
            sanitizePositiveIntegerDraft(
              e.target.value,
              PIPELINE_PERIOD_DAYS_MIN,
              PIPELINE_PERIOD_DAYS_MAX
            ),
            onChange,
            extra
          )
        }
        onBlur={(e) => commitPeriodDays(task, e.target.value, onChange, extra)}
      />
    </div>
  );

  const renderRatioPercent = (
    trainKey: string,
    valKey: string,
    trainValue: unknown,
    valValue: unknown,
    trainFallback: number,
    valFallback: number,
    onPatch: (trainRaw: string, valRaw: string) => void
  ) => {
    const siblingTrain = trainValue === '' ? '' : numberInputDisplay(trainValue, trainFallback);
    const siblingVal = valValue === '' ? '' : numberInputDisplay(valValue, valFallback);
    return (
      <>
        {renderNumber(trainKey, 'Train ratio (0–1)', trainValue, trainFallback, (raw) =>
          onPatch(raw, siblingVal), {
          min: UNIT_RATIO_MIN,
          max: UNIT_RATIO_MAX,
          step: UNIT_RATIO_STEP,
          enforceBounds: true,
        })}
        {renderNumber(valKey, 'Validation ratio (0–1)', valValue, valFallback, (raw) =>
          onPatch(siblingTrain, raw), {
          min: UNIT_RATIO_MIN,
          max: UNIT_RATIO_MAX,
          step: UNIT_RATIO_STEP,
          enforceBounds: true,
        })}
      </>
    );
  };

  let body: React.ReactNode = null;

  if (type === 'load_integration_datasets') {
    const reactions = Array.isArray(c.reactions) ? (c.reactions as string[]) : ['like', 'dislike'];
    const rw = reactionWeightsToForm(c.reaction_weights);
    const loadUseDateRange = hasTrainingPeriodObject(c);
    const loadPeriod = getTrainingPeriodFromConfig(c);
    body = (
      <>
        <div className="pipeline-task-config__field pipeline-task-config__field--wide">
          {renderLabel(`pt-${taskIndex}-ds-name`, 'Dataset name')}
          <input
            id={`pt-${taskIndex}-ds-name`}
            type="text"
            className="pipeline-task-config__input"
            value={cfgStr(c.dataset_name_template, 'integration-dataset')}
            disabled={fieldsDisabled}
            onChange={(e) => patchConfig(task, { dataset_name_template: e.target.value }, onChange)}
          />
          <p className="pipeline-task-config__muted">
            Backend auto-increments: integration-dataset, integration-dataset_1, …
          </p>
        </div>
        <PeriodModeToggle
          id={`pt-${taskIndex}-load-period`}
          useDateRange={loadUseDateRange}
          disabled={fieldsDisabled}
          onSelectDays={() => disableTrainingPeriodDateRange(task, c, onChange)}
          onSelectRange={() => enableTrainingPeriodDateRange(task, c, onChange)}
        />
        {!loadUseDateRange ? (
          <>
            {renderPeriodDays(`pt-${taskIndex}-period`, c.period_days)}
            {periodDaysValidationError ? (
              <p className="pipeline-task-config__inline-error" role="alert">
                {periodDaysValidationError}
              </p>
            ) : null}
          </>
        ) : (
          <>
          <div className="pipeline-task-config__date-row">
            <DateTimePicker
              label="From"
              value={loadPeriod.from || ''}
              disabled={fieldsDisabled}
              onChange={(v) => patchTrainingPeriod(task, c, { from: v }, onChange)}
            />
            <DateTimePicker
              label="To"
              value={loadPeriod.to || ''}
              disabled={fieldsDisabled}
              onChange={(v) => patchTrainingPeriod(task, c, { to: v }, onChange)}
              min={loadPeriod.from || undefined}
            />
          </div>
          {periodValidationError ? (
            <p className="pipeline-task-config__inline-error" role="alert">
              {periodValidationError}
            </p>
          ) : null}
          </>
        )}
        {renderFieldGrid(
          <>
            <div className="pipeline-task-config__field">
              {renderLabel(`pt-${taskIndex}-regime`, 'Regime')}
              <select
                id={`pt-${taskIndex}-regime`}
                className="pipeline-task-config__input"
                value={cfgStr(c.regime, 'overlap')}
                disabled={fieldsDisabled}
                onChange={(e) => patchConfig(task, { regime: e.target.value }, onChange)}
              >
                <option value="no_overlap">No overlap</option>
                <option value="overlap">Overlap</option>
              </select>
            </div>
            {renderNumber(`pt-${taskIndex}-regime-pct`, 'Regime %', c.regime_percent, 30, (raw) =>
              patchConfig(task, { regime_percent: raw }, onChange)
            )}
            {renderRatioPercent(
              `pt-${taskIndex}-train`,
              `pt-${taskIndex}-val`,
              c.train_ratio,
              c.validation_ratio,
              0.8,
              0.2,
              (t, v) => patchConfig(task, { train_ratio: t, validation_ratio: v }, onChange)
            )}
          </>
        )}
        <HistoryContextControl
          limited={cfgBool(c.limit_history, true)}
          onLimitedChange={(v) => patchConfig(task, { limit_history: v }, onChange)}
          depthValue={numberInputDisplay(c.history_depth, 20)}
          onDepthValueChange={(v) => patchConfig(task, { history_depth: v }, onChange)}
          depthInputId={`pt-${taskIndex}-history`}
          disabled={fieldsDisabled}
        />
        {!task.enabled ? (
          <p className="pipeline-task-config__hint pipeline-task-config__hint--info">
            Turn on <strong>Enabled</strong> for this step to edit export filters.
          </p>
        ) : null}
        <div className="pipeline-task-config__toggle-grid">
          <BoolToggleRow
            on={cfgBool(c.comment, true)}
            disabled={fieldsDisabled}
            onToggle={() => patchConfig(task, { comment: !cfgBool(c.comment, true) }, onChange)}
            label="Comment"
          />
          <BoolToggleRow
            on={cfgBool(c.groundtruth, true)}
            disabled={fieldsDisabled}
            onToggle={() => patchConfig(task, { groundtruth: !cfgBool(c.groundtruth, true) }, onChange)}
            label="Ground truth"
          />
          <BoolToggleRow
            on={reactions.includes('like')}
            disabled={fieldsDisabled}
            onToggle={() => {
              const next = reactions.includes('like')
                ? reactions.filter((entry) => entry !== 'like')
                : Array.from(new Set([...reactions, 'like']));
              patchConfig(task, { reactions: next }, onChange);
            }}
            label="Like"
          />
          <BoolToggleRow
            on={reactions.includes('dislike')}
            disabled={fieldsDisabled}
            onToggle={() => {
              const next = reactions.includes('dislike')
                ? reactions.filter((entry) => entry !== 'dislike')
                : Array.from(new Set([...reactions, 'dislike']));
              patchConfig(task, { reactions: next }, onChange);
            }}
            label="Dislike"
          />
        </div>
        <FineTuningReactionWeightsFields
          idPrefix={`pt-${taskIndex}-rw`}
          value={rw}
          disabled={fieldsDisabled}
          onChange={(form) => {
            const weights = buildReactionWeightsFromForm(form);
            patchConfig(task, { reaction_weights: weights ?? {} }, onChange);
          }}
        />
      </>
    );
  } else if (type === 'generate_vuser_datasets') {
    const vuIds = Array.isArray(c.virtual_user_ids) ? (c.virtual_user_ids as string[]) : [];
    const vaIds = Array.isArray(c.virtual_admin_ids) ? (c.virtual_admin_ids as string[]) : [];
    const rw = reactionWeightsToForm(c.reaction_weights);
    body = (
      <>
        <div className="pipeline-task-config__field pipeline-task-config__field--wide">
          {renderLabel(`pt-${taskIndex}-exp-name`, 'Experiment name')}
          <input
            id={`pt-${taskIndex}-exp-name`}
            type="text"
            className="pipeline-task-config__input"
            value={cfgStr(c.experiment_name, 'pipeline-vuser-dataset')}
            disabled={fieldsDisabled}
            onChange={(e) => patchConfig(task, { experiment_name: e.target.value }, onChange)}
          />
          <p className="pipeline-task-config__muted">
            Backend auto-increments: pipeline-vuser-dataset, pipeline-vuser-dataset_1, …
          </p>
        </div>
        <div className="pipeline-task-config__field pipeline-task-config__field--wide">
          <span className="pipeline-task-config__label">Virtual users (type=user only)</span>
          <MultiSelect
            options={userOptions}
            value={vuIds}
            onChange={(ids) => patchConfig(task, { virtual_user_ids: ids }, onChange)}
            placeholder="Select virtual users…"
            portal
            disabled={fieldsDisabled}
            maxDropdownHeight={480}
            dropdownClassName="multi-select-dropdown--pipeline-datasets"
          />
          {vuserValidationError ? (
            <p className="pipeline-task-config__inline-error" role="alert">
              {vuserValidationError}
            </p>
          ) : null}
        </div>
        <div className="pipeline-task-config__field pipeline-task-config__field--wide">
          <span className="pipeline-task-config__label">Virtual admins (type=admin only)</span>
          <MultiSelect
            options={adminOptions}
            value={vaIds}
            onChange={(ids) => patchConfig(task, { virtual_admin_ids: ids }, onChange)}
            placeholder="Select virtual admins…"
            portal
            disabled={fieldsDisabled}
            maxDropdownHeight={480}
            dropdownClassName="multi-select-dropdown--pipeline-datasets"
          />
        </div>
        {renderFieldGrid(
          <>
            {renderNumber(`pt-${taskIndex}-dialogs`, 'Dialog count', c.dialog_count, 1, (raw) =>
              patchConfig(task, { dialog_count: raw }, onChange),
              {
                min: DATASET_GENERATION_MIN_DIALOGS,
                max: DATASET_GENERATION_MAX_DIALOGS,
                integer: true,
                enforceBounds: true,
              }
            )}
            {renderNumber(`pt-${taskIndex}-msgs`, 'Messages per dialog', c.messages_per_dialog, 5, (raw) =>
              patchConfig(task, { messages_per_dialog: raw }, onChange),
              {
                min: DATASET_GENERATION_MIN_MESSAGES_PER_DIALOG,
                max: DATASET_GENERATION_MAX_MESSAGES_PER_DIALOG,
                integer: true,
                enforceBounds: true,
              }
            )}
            {renderRatioPercent(
              `pt-${taskIndex}-vtrain`,
              `pt-${taskIndex}-vval`,
              c.train_ratio,
              c.validation_ratio,
              0.8,
              0.2,
              (t, v) => patchConfig(task, { train_ratio: t, validation_ratio: v }, onChange)
            )}
          </>
        )}
        {renderVuserTokenEstimatePanel()}
        <div className="pipeline-task-config__field pipeline-task-config__field--wide">
          {renderLabel(`pt-${taskIndex}-first`, 'First message')}
          <textarea
            id={`pt-${taskIndex}-first`}
            className="pipeline-task-config__textarea"
            rows={2}
            value={cfgStr(c.first_message, '')}
            disabled={fieldsDisabled}
            onChange={(e) => patchConfig(task, { first_message: e.target.value }, onChange)}
          />
        </div>
        <HistoryContextControl
          limited={cfgBool(c.limit_history, true)}
          onLimitedChange={(v) => patchConfig(task, { limit_history: v }, onChange)}
          depthValue={numberInputDisplay(c.history_depth, 20)}
          onDepthValueChange={(v) => patchConfig(task, { history_depth: v }, onChange)}
          depthInputId={`pt-${taskIndex}-vhistory`}
          disabled={fieldsDisabled}
        />
        <FineTuningReactionWeightsFields
          idPrefix={`pt-${taskIndex}-vrw`}
          value={rw}
          disabled={fieldsDisabled}
          onChange={(form) => {
            const weights = buildReactionWeightsFromForm(form);
            patchConfig(task, { reaction_weights: weights ?? {} }, onChange);
          }}
        />
      </>
    );
  } else if (type === 'select_training_datasets') {
    body = (
      <>
        <p className="pipeline-task-config__hint">
          Selects datasets from this pipeline run and/or registry for integration <code>{integrationId}</code>.
        </p>
        {!task.enabled ? (
          <p className="pipeline-task-config__hint pipeline-task-config__hint--info">
            Turn on <strong>Enabled</strong> for this step to choose dataset sources.
          </p>
        ) : null}
        <div className="pipeline-task-config__toggle-grid">
          <BoolToggleRow
            on={datasetTypes.includes('integration')}
            disabled={fieldsDisabled}
            onToggle={() => {
              const nextEnabled = !datasetTypes.includes('integration');
              const next = nextEnabled
                ? Array.from(new Set([...datasetTypes, 'integration']))
                : datasetTypes.filter((entry) => entry !== 'integration');
              if (next.length === 0) return;
              patchConfig(task, { dataset_types: next, integration_id: integrationId }, onChange);
            }}
            label="Integration datasets"
          />
          <BoolToggleRow
            on={datasetTypes.includes('vuser')}
            disabled={fieldsDisabled}
            onToggle={() => {
              const nextEnabled = !datasetTypes.includes('vuser');
              const next = nextEnabled
                ? Array.from(new Set([...datasetTypes, 'vuser']))
                : datasetTypes.filter((entry) => entry !== 'vuser');
              if (next.length === 0) return;
              patchConfig(task, { dataset_types: next, integration_id: integrationId }, onChange);
            }}
            label="VUser datasets"
          />
        </div>
        <PeriodModeToggle
          id={`pt-${taskIndex}-select-period`}
          useDateRange={selectUseDateRange}
          disabled={fieldsDisabled}
          onSelectDays={() =>
            disableTrainingPeriodDateRange(task, c, onChange, { integration_id: integrationId })
          }
          onSelectRange={() =>
            enableTrainingPeriodDateRange(task, c, onChange, { integration_id: integrationId })
          }
        />
        {!selectUseDateRange ? (
          <>
            {renderPeriodDays(`pt-${taskIndex}-pdays`, c.period_days, { integration_id: integrationId })}
            {periodDaysValidationError ? (
              <p className="pipeline-task-config__inline-error" role="alert">
                {periodDaysValidationError}
              </p>
            ) : null}
          </>
        ) : (
          <>
          <div className="pipeline-task-config__date-row">
            <DateTimePicker
              label="From"
              value={selectPeriod.from || ''}
              disabled={fieldsDisabled}
              onChange={(v) =>
                patchTrainingPeriod(task, c, { from: v }, onChange, { integration_id: integrationId })
              }
            />
            <DateTimePicker
              label="To"
              value={selectPeriod.to || ''}
              disabled={fieldsDisabled}
              onChange={(v) =>
                patchTrainingPeriod(task, c, { to: v }, onChange, { integration_id: integrationId })
              }
              min={selectPeriod.from || undefined}
            />
          </div>
          {periodValidationError ? (
            <p className="pipeline-task-config__inline-error" role="alert">
              {periodValidationError}
            </p>
          ) : null}
          </>
        )}
        <RegistryDatasetsPreview
          datasets={filteredRegistryDatasets}
          loading={connectorDatasetsLoading}
        />
      </>
    );
  } else if (type === 'config_tuning') {
    const selectionScenario = resolveSelectionScenario(c);
    const taskApproach = normalizeOptimizationApproach(
      typeof c.approach === 'string' && c.approach.trim() ? c.approach : configFromConfigurator?.approach
    );
    const tuningConfiguratorConfig = configFromConfigurator
      ? { ...configFromConfigurator, approach: taskApproach }
      : configFromConfigurator;
    const evaluationMode = normalizeEvaluationMode(c.evaluation_mode);
    const evaluatorParams = parseEvaluatorParamsForm(c.evaluator_params);

    const patchSelectionScenario = (value: SelectionScenario) => {
      patchConfig(
        task,
        {
          selection_scenario: value,
          optimize_by_token: deriveOptimizeByTokenFromSelectionScenario(value),
        },
        onChange
      );
    };

    const patchEvaluationMode = (value: EvaluationMode) => {
      const nextParams = mergeEvaluatorParamsFormForMode(value, evaluatorParams);
      patchConfig(
        task,
        {
          evaluation_mode: value,
          evaluator_params: buildEvaluatorParamsPayload(value, nextParams, { includeDirection: true }),
        },
        onChange
      );
    };

    const patchEvaluatorParams = (next: EvaluatorParamsFormState) => {
      const mode = taskApproach === 'MUTATIONS' ? 'config-scorer' : evaluationMode;
      patchConfig(
        task,
        {
          evaluator_params: buildEvaluatorParamsPayload(mode, next, { includeDirection: true }),
        },
        onChange
      );
    };

    body = tuningConfiguratorConfig ? (
      <>
        {hasSelectTrainingStep && (
          <p className="pipeline-task-config__hint pipeline-task-config__hint--info">
            Datasets are supplied by the <strong>Select Training Datasets</strong> step — do not set dataset IDs here.
          </p>
        )}
        <ConnectorConfigTuningFields
          currentConfig={tuningConfiguratorConfig}
          onConfigChange={(next) => {
            const approach = normalizeOptimizationApproach(next.approach);
            if (approach === 'MUTATIONS') {
              const nextParams = mergeEvaluatorParamsFormForMode('config-scorer', evaluatorParams);
              patchConfig(
                task,
                {
                  approach,
                  evaluation_mode: 'config-scorer',
                  evaluator_params: buildEvaluatorParamsPayload('config-scorer', nextParams, {
                    includeDirection: true,
                  }),
                },
                onChange
              );
              return;
            }
            patchConfig(task, { approach }, onChange);
          }}
          disabled={fieldsDisabled}
          datasets={[]}
          selectedDatasets={[]}
          onDatasetsChange={() => undefined}
          hideDatasetPicker={hasSelectTrainingStep}
          iterations={optimizationIterations}
          onIterationsChange={onOptimizationIterationsChange}
          optimizationPolicyUi={optimizationPolicyUi}
          onOptimizationPolicyUiChange={onOptimizationPolicyUiChange}
          selectionScenario={selectionScenario}
          onSelectionScenarioChange={patchSelectionScenario}
          evaluationMode={evaluationMode}
          onEvaluationModeChange={patchEvaluationMode}
          evaluatorParams={evaluatorParams}
          onEvaluatorParamsChange={patchEvaluatorParams}
          availableModels={configuratorAvailableModels}
        />
        <p className="pipeline-task-config__muted">
          Optimization uses the integration configurator state and policy above when the pipeline is saved.
        </p>
        {renderPreviewActions(
          <>
            <PipelinePreviewDatasetPicker
              id={`pt-${taskIndex}-config-preview-datasets`}
              label="Datasets for preview"
              hint={
                hasSelectTrainingStep
                  ? 'At run time datasets come from Select Training Datasets. Pick datasets here to preview before launch.'
                  : 'Choose registry dataset labels to estimate optimization cost.'
              }
              options={connectorDatasetLabelOptions}
              selected={configPreviewDatasetNames}
              onChange={setConfigPreviewDatasetNames}
              loading={connectorDatasetsLoading}
            />
            <button
              type="button"
              className="pipeline-btn pipeline-btn--ghost pipeline-task-config__preview-btn"
              disabled={configPreviewLoading || !configFromConfigurator}
              onClick={() => void handleConfigTuningPreview()}
            >
              <i className={`fas ${configPreviewLoading ? 'fa-spinner fa-spin' : 'fa-calculator'}`} aria-hidden />
              {configPreviewLoading ? 'Loading preview…' : 'Preview config tuning'}
            </button>
            {configPreviewError ? (
              <p className="pipeline-task-config__inline-error" role="alert">
                {configPreviewError}
              </p>
            ) : null}
            {configPreviewResult ? <OptimizationPreviewResultView data={configPreviewResult} /> : null}
          </>
        )}
      </>
    ) : (
      <p className="pipeline-task-config__hint">Load the integration configurator before configuring this step.</p>
    );
  } else if (type === 'fine_tuning') {
    const agents = Array.isArray(c.agents) ? c.agents : [];
    const groupByBaseModel = cfgBool(c.group_by_base_model, false);
    const modelNamePrefix = resolveFineTuningModelNamePrefix(c.model_name);
    const baseModelGroups = groupByBaseModel
      ? listFineTuningBaseModelGroups(
          agents as Array<{ agent_name: string; base_model?: string }>,
          modelNamePrefix
        )
      : [];
    body = (
      <>
        {hasSelectTrainingStep && (
          <p className="pipeline-task-config__hint pipeline-task-config__hint--info">
            Datasets are supplied by the <strong>Select Training Datasets</strong> step.
          </p>
        )}
        {renderFieldGrid(
          <>
            <div className="pipeline-task-config__field pipeline-task-config__field--wide">
              {renderLabel(`pt-${taskIndex}-ft-model`, 'Model name prefix')}
              <input
                id={`pt-${taskIndex}-ft-model`}
                type="text"
                className="pipeline-task-config__input"
                value={cfgStr(c.model_name, 'pipeline-ft')}
                maxLength={FINE_TUNING_MODEL_NAME_MAX_LEN}
                disabled={fieldsDisabled}
                onChange={(e) =>
                  patchConfig(
                    task,
                    { model_name: e.target.value.slice(0, FINE_TUNING_MODEL_NAME_MAX_LEN) },
                    onChange
                  )
                }
              />
              <p className="pipeline-task-config__muted">
                {groupByBaseModel
                  ? 'Grouped fine-tuning uses one derived model name per base model (shown below).'
                  : 'Each trainable agent gets a derived model name from this prefix when the pipeline is saved.'}
              </p>
            </div>
            <div className="pipeline-task-config__field pipeline-task-config__field--wide">
              <span className="pipeline-task-config__label">Group by base model</span>
              <div
                className="pipeline-period-toggle__segment"
                role="radiogroup"
                aria-label="Group by base model"
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={groupByBaseModel}
                  className={`pipeline-period-toggle__btn${groupByBaseModel ? ' pipeline-period-toggle__btn--active' : ''}`}
                  disabled={fieldsDisabled}
                  onClick={() => patchConfig(task, { group_by_base_model: true }, onChange)}
                >
                  Yes
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={!groupByBaseModel}
                  className={`pipeline-period-toggle__btn${!groupByBaseModel ? ' pipeline-period-toggle__btn--active' : ''}`}
                  disabled={fieldsDisabled}
                  onClick={() => patchConfig(task, { group_by_base_model: false }, onChange)}
                >
                  No
                </button>
              </div>
            </div>
            <div className="pipeline-task-config__field pipeline-task-config__field--wide">
              <BoolToggleRow
                on={cfgBool(c.merge_train_validation, true)}
                disabled={fieldsDisabled}
                onToggle={() =>
                  patchConfig(
                    task,
                    { merge_train_validation: !cfgBool(c.merge_train_validation, true) },
                    onChange
                  )
                }
                label="Merge train & validation"
              />
            </div>
          </>
        )}
        <HistoryContextControl
          limited={cfgBool(c.limit_history, true)}
          onLimitedChange={(v) => patchConfig(task, { limit_history: v }, onChange)}
          depthValue={numberInputDisplay(c.history_depth, 20)}
          onDepthValueChange={(v) => patchConfig(task, { history_depth: v }, onChange)}
          depthInputId={`pt-${taskIndex}-fthistory`}
          disabled={fieldsDisabled}
        />
        {fineTuningValidationError ? (
          <p className="pipeline-task-config__inline-error" role="alert">
            {fineTuningValidationError}
          </p>
        ) : null}
        <div className="pipeline-task-config__agents">
          <span className="pipeline-task-config__label">Trainable agents ({agents.length})</span>
          {agents.length === 0 ? (
            <p className="pipeline-task-config__muted">No trainable agents in configurator.</p>
          ) : groupByBaseModel && baseModelGroups.length > 0 ? (
            <ul className="pipeline-task-config__dataset-list">
              {baseModelGroups.map((group) => (
                <li key={group.base_model}>
                  <strong>{group.base_model}</strong>
                  <span aria-hidden> · </span>
                  FT model: <code>{group.model_name}</code>
                  <span aria-hidden> · </span>
                  {group.agent_names.join(', ')}
                </li>
              ))}
            </ul>
          ) : (
            <ul className="pipeline-task-config__dataset-list">
              {(agents as Array<Record<string, unknown>>).map((a, i) => (
                <li key={String(a.agent_id || i)}>
                  {String(a.agent_name || 'Agent')} · {String(a.base_model || '')}
                </li>
              ))}
            </ul>
          )}
        </div>
        {renderPreviewActions(
          <>
            <PipelinePreviewDatasetPicker
              id={`pt-${taskIndex}-ft-preview-datasets`}
              label="Datasets for preview"
              hint={
                hasSelectTrainingStep
                  ? 'At run time dataset IDs come from Select Training Datasets (or Generate VUser Datasets). Pick saved datasets here to preview before launch.'
                  : 'Select saved registry datasets to preview token usage and agent readiness.'
              }
              options={connectorDatasetIdOptions}
              selected={ftPreviewDatasetIds}
              onChange={setFtPreviewDatasetIds}
              loading={connectorDatasetsLoading}
            />
            <button
              type="button"
              className="pipeline-btn pipeline-btn--ghost pipeline-task-config__preview-btn"
              disabled={ftPreviewLoading || ftPreviewDatasetIds.length === 0}
              onClick={() => void handleFineTuningPreview()}
            >
              <i className={`fas ${ftPreviewLoading ? 'fa-spinner fa-spin' : 'fa-eye'}`} aria-hidden />
              {ftPreviewLoading ? 'Loading preview…' : 'Preview fine-tuning'}
            </button>
            {ftPreviewError ? (
              <p className="pipeline-task-config__inline-error" role="alert">
                {ftPreviewError}
              </p>
            ) : null}
            {ftPreviewResult ? <TrainingPreviewResultView data={ftPreviewResult} /> : null}
          </>
        )}
      </>
    );
  }

  return (
    <div
      className={`pipeline-task-config ${task.enabled ? '' : 'pipeline-task-config--disabled'}`}
      style={{ '--task-accent': catalog?.accent || '#5B7893' } as React.CSSProperties}
      data-pipeline-task-index={taskIndex}
    >
      <div className="pipeline-task-config__head">
        <span className="pipeline-task-config__step">{taskIndex + 1}</span>
        <strong>{formatPipelineTaskType(type)}</strong>
        <PipelineToggleSwitch
          className="pipeline-task-config__enable"
          checked={task.enabled}
          onChange={(enabled) => onChange({ ...task, enabled })}
          label="Enabled"
        />
      </div>
      <div className="pipeline-task-config__body">{body}</div>
    </div>
  );
};

export default PipelineTaskConfigEditor;
